import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMatches } from '@tanstack/react-router';
import {
  controlNativeTooltips,
  forgetPersonAppearance,
  onPersonAppearance,
  personAppearance,
  receivePersonAppearance,
  setPersonAppearanceSaver,
} from '@paige/capturepoint';
import { accentDefaults, useTheme } from '@paige/ui';
import { useAuth } from '../auth/AuthBoundary';
import { useDataSource } from '../api/data-source';
import { PICKED_VARS, paigeLayerVars, tokenAccent } from './appearance-look';
import { adoptBrowserAppearance } from './appearance-adoption';
import { inShell } from './in-shell';

/** The person's appearance in the cache: theirs alone, so the key carries who. */
export const appearanceKey = (user: string | null) => ['me', 'appearance', user] as const;

/**
 * The person's one accent colour and Show tooltips, on every screen (Dustin, 2026-10-20; docs/engineering/engagement.md,
 * "Appearance"). Read once per sign-in from `/me/appearance` and handed to the workbench's store
 * (`@paige/capturepoint` person-appearance), which the workbench's Appearance page changes and saves through the
 * saver given here; every change, there or anywhere, repaints the dashboard: the Paige layer's accent
 * (`paigeLayerVars`), the packages/ui tokens (ThemeProvider `setAccent`), and the native tooltips while no workbench
 * owns them. Renders nothing.
 */
export function PersonAppearance(): ReactNode {
  const user = useAuth().principal?.id ?? null;
  useServerAppearance(user);
  usePaintedAccent();
  useDashboardTooltips();
  return null;
}

function useServerAppearance(user: string | null): void {
  const ds = useDataSource();
  const queryClient = useQueryClient();
  const read = useQuery({
    queryKey: appearanceKey(user),
    queryFn: () => ds.appearance.get(),
    enabled: user !== null,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (user === null) return undefined;
    setPersonAppearanceSaver(async (next) => {
      const saved = await ds.appearance.save(next);
      queryClient.setQueryData(appearanceKey(user), saved);
      return saved;
    });
    return () => forgetPersonAppearance();
  }, [ds, queryClient, user]);
  useEffect(() => {
    if (!read.data) return;
    receivePersonAppearance(read.data);
    void adoptBrowserAppearance(read.data);
  }, [read.data]);
}

const useAppearance = () => useSyncExternalStore(onPersonAppearance, personAppearance);

/** The picked colour of the theme in force, on the root for the Paige layer and in the ui tokens. */
function usePaintedAccent(): void {
  const { resolved, setAccent } = useTheme();
  const appearance = useAppearance();
  const picked = appearance.accent[resolved];
  useEffect(() => {
    const root = document.documentElement;
    const vars = paigeLayerVars(picked, resolved);
    for (const name of PICKED_VARS) {
      const value = vars[name];
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    }
    const tokens = tokenAccent(picked, resolved);
    setAccent(tokens?.hue ?? accentDefaults.hue, tokens?.chroma ?? accentDefaults.chroma);
  }, [picked, resolved, setAccent]);
}

/**
 * Show tooltips off hides every native title on Paige's own screens too. Inside a job the workbench keeps its own
 * hold on them (installTooltipPreference, reading the same switch), so this one lets go there: one owner at a time.
 */
function useDashboardTooltips(): void {
  const workbench = useMatches({ select: (matches) => matches.some((m) => inShell(m.routeId)) });
  const { tooltips } = useAppearance();
  useEffect(() => {
    if (workbench) return undefined;
    const native = controlNativeTooltips(document.documentElement);
    native.apply(tooltips);
    return () => native.dispose();
  }, [workbench, tooltips]);
}
